import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRuleStore } from '../stores/ruleStore';
import { useDataStore } from '../stores/dataStore';
import { useToastActions } from '../components/ui/Toast';
import {
  Plus,
  Trash2,
  Edit3,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  Zap,
} from 'lucide-react';
import type { RuleStatus } from '../types/rule';

export function RuleManagementPage() {
  const { t } = useTranslation();
  const {
    rules,
    selectedRule,
    applications,
    isLoading,
    isApplying,
    fetchRules,
    setSelectedRule,
    deleteRule,
    fetchApplications,
    detectAndApply,
  } = useRuleStore();

  const { dataFiles, selectedData, setSelectedData, fetchDataList } = useDataStore();
  const toast = useToastActions();
  const [activeTab, setActiveTab] = useState<'rules' | 'applications'>('rules');

  useEffect(() => {
    fetchRules();
    fetchDataList();
  }, [fetchRules, fetchDataList]);

  useEffect(() => {
    if (selectedRule) {
      fetchApplications(selectedRule.id);
    }
  }, [selectedRule, fetchApplications]);

  const handleDetectAndApply = async (fileId: string) => {
    await detectAndApply(fileId, true);
  };

  const getStatusBadge = (status: RuleStatus) => {
    const styles = {
      active: 'bg-green-100 text-green-700',
      inactive: 'bg-gray-100 text-gray-700',
      archived: 'bg-yellow-100 text-yellow-700',
    };
    const labels = {
      active: t('rules.statusActive'),
      inactive: t('rules.statusInactive'),
      archived: t('rules.statusArchived'),
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* 页面标题 */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">{t('rules.title')}</h1>
          <p className="mt-1 text-gray-600">{t('rules.subtitle')}</p>
        </div>

        {/* 自动检测区域 */}
        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl p-6 mb-8 text-white">
          <div className="flex items-center gap-3 mb-4">
            <Zap className="w-6 h-6" />
            <h2 className="text-lg font-semibold">{t('rules.smartMatch')}</h2>
          </div>
          <p className="text-blue-100 mb-4">
            {t('rules.smartMatchDesc')}
          </p>
          <div className="flex gap-4">
            <select
              className="flex-1 px-4 py-2 rounded-lg text-gray-900 bg-white"
              value={selectedData?.id || ''}
              onChange={(e) => {
                const file = dataFiles.find((f) => f.id === e.target.value);
                setSelectedData(file || null);
              }}
            >
              <option value="">{t('rules.selectFile')}</option>
              {dataFiles.map((file) => (
                <option key={file.id} value={file.id}>
                  {file.name} ({file.rowCount.toLocaleString()} 行)
                </option>
              ))}
            </select>
            <button
              onClick={() => selectedData && handleDetectAndApply(selectedData.id)}
              disabled={!selectedData || isApplying}
              className="
                flex items-center gap-2 px-6 py-2
                bg-white text-blue-600 font-medium rounded-lg
                hover:bg-blue-50 disabled:opacity-50
                transition-colors duration-200
              "
            >
              {isApplying ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('rules.detecting')}
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4" />
                  {t('rules.detectApply')}
                </>
              )}
            </button>
          </div>

        </div>

        {/* 标签页 */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="border-b border-gray-200">
            <div className="flex">
              <button
                onClick={() => setActiveTab('rules')}
                className={`
                  px-6 py-4 text-sm font-medium border-b-2
                  ${
                    activeTab === 'rules'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }
                `}
              >
                {t('rules.rulesList')} ({rules.length})
              </button>
              <button
                onClick={() => setActiveTab('applications')}
                className={`
                  px-6 py-4 text-sm font-medium border-b-2
                  ${
                    activeTab === 'applications'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }
                `}
              >
                {t('rules.applications')} ({applications.length})
              </button>
            </div>
          </div>

          <div className="p-6">
            {activeTab === 'rules' ? (
              <div className="space-y-4">
                {/* 创建规则按钮 */}
                <div className="flex justify-end">
                  <button
                    onClick={() => toast.info(t('rules.devNotice'), t('rules.devTitle'))}
                    className="
                      flex items-center gap-2 px-4 py-2
                      bg-blue-600 text-white font-medium rounded-lg
                      hover:bg-blue-700 transition-colors duration-200
                    "
                  >
                    <Plus className="w-4 h-4" />
                    {t('rules.createRule')}
                  </button>
                </div>

                {/* 规则列表 */}
                {isLoading ? (
                  <div className="text-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-600" />
                  </div>
                ) : rules.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <p>{t('rules.noRules')}</p>
                    <p className="text-sm mt-1">{t('rules.noRulesDesc')}</p>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {rules.map((rule) => (
                      <div
                        key={rule.id}
                        onClick={() => setSelectedRule(rule)}
                        className={`
                          p-4 rounded-lg border cursor-pointer
                          transition-all duration-200
                          ${
                            selectedRule?.id === rule.id
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                          }
                        `}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-gray-900">
                                {rule.name}
                              </h3>
                              {getStatusBadge(rule.status)}
                            </div>
                            <p className="text-sm text-gray-500 mt-1">
                              {rule.description || t('rules.noDescription')}
                            </p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                              <span>
                                {t('rules.columnFeatures', { count: rule.inputSchema.columns.length })}
                              </span>
                              <span>{t('rules.usageCount', { count: rule.usageCount })}</span>
                              {rule.lastUsedAt && (
                                <span>
                                  {t('rules.lastUsed')}:{' '}
                                  {new Date(rule.lastUsedAt).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                // TODO: 编辑规则
                              }}
                              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                            >
                              <Edit3 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteRule(rule.id);
                              }}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {applications.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <p>{t('rules.noApplications')}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {applications.map((app) => (
                      <div
                        key={app.id}
                        className="p-4 rounded-lg border border-gray-200"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {app.status === 'success' && (
                              <CheckCircle2 className="w-5 h-5 text-green-500" />
                            )}
                            {app.status === 'failed' && (
                              <XCircle className="w-5 h-5 text-red-500" />
                            )}
                            {app.status === 'pending' && (
                              <Clock className="w-5 h-5 text-yellow-500" />
                            )}
                            {app.status === 'running' && (
                              <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                            )}
                            <div>
                              <p className="font-medium text-gray-900">
                                {t('rules.appRecord', { id: app.id.slice(0, 8) })}
                              </p>
                              <p className="text-sm text-gray-500">
                                {new Date(app.createdAt).toLocaleString()}
                              </p>
                            </div>
                          </div>
                          <span
                            className={`
                              px-2 py-1 text-xs font-medium rounded-full
                              ${
                                app.status === 'success'
                                  ? 'bg-green-100 text-green-700'
                                  : app.status === 'failed'
                                  ? 'bg-red-100 text-red-700'
                                  : app.status === 'running'
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-yellow-100 text-yellow-700'
                              }
                            `}
                          >
                            {app.status === 'success' && t('rules.appSuccess')}
                            {app.status === 'failed' && t('rules.appFailed')}
                            {app.status === 'pending' && t('rules.appPending')}
                            {app.status === 'running' && t('rules.appRunning')}
                          </span>
                        </div>
                        {app.errorMessage && (
                          <p className="mt-2 text-sm text-red-600">
                            {app.errorMessage}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
